import { useEffect, useState } from "react";
import { getCurrentBusinessContext, saveProfileProgress } from "../application/round02-service";
import {
  DATABASE_VERSION,
  deleteAllLocalData,
  initializeDatabase,
  type CEOOSDatabase,
} from "../db/database";
import { isValidTimeZone } from "../domain/business-date";
import type { CEOProfile, ConstitutionVersion } from "../domain/types";
import {
  commitFullRestore,
  createFullBackup,
  preflightFullRestore,
  type RestorePreflight,
} from "../import-export/backup";
import {
  commitBoardContextImport,
  preflightBoardContextImport,
} from "../import-export/board-import";
import { downloadJson, readJsonFile } from "../import-export/file";
import { FULL_BACKUP_WARNING, LOCAL_SECURITY_BOUNDARY, safeErrorMessage } from "../privacy/policy";
import { checkForPWAUpdate } from "../pwa";

export function Settings({
  database,
  profile,
  onProfileChange,
  onContextChange,
  onDataReset,
}: {
  database: CEOOSDatabase;
  profile: CEOProfile;
  onProfileChange: (profile: CEOProfile) => void;
  onContextChange: () => Promise<void>;
  onDataReset: () => Promise<void>;
}) {
  const [timezoneDraft, setTimezoneDraft] = useState(profile.businessTimezone);
  const [timezoneChangePending, setTimezoneChangePending] = useState(false);
  const [constitution, setConstitution] = useState<ConstitutionVersion>();
  const [restorePreflight, setRestorePreflight] = useState<RestorePreflight>();
  const [notice, setNotice] = useState("");

  useEffect(() => {
    void database.constitutions.get(profile.currentConstitutionVersionId).then(setConstitution);
  }, [database, profile.currentConstitutionVersionId]);

  const changeTimezone = async () => {
    if (timezoneDraft === profile.businessTimezone) return;
    if (!isValidTimeZone(timezoneDraft)) {
      setNotice("业务时区无效。请选择 IANA 时区，例如 Asia/Shanghai。");
      return;
    }
    try {
      const next = await saveProfileProgress(database, {
        ...profile,
        businessTimezone: timezoneDraft,
      });
      onProfileChange(next);
      setTimezoneChangePending(false);
      setNotice("业务时区已明确更改；历史复盘日期保持不变。");
    } catch (error) {
      setNotice(safeErrorMessage(error));
    }
  };

  const importFile = async (file: File) => {
    try {
      const value = await readJsonFile(file);
      if (
        value &&
        typeof value === "object" &&
        (value as Record<string, unknown>).schema_name === "board-context-import"
      ) {
        const preflight = await preflightBoardContextImport(database, value, {
          name: file.name,
          size: file.size,
          lastModified: file.lastModified,
        });
        if (preflight.result.status === "rejected") {
          setNotice(
            `BoardContext 未导入：${preflight.result.errors
              .map((item) => `${item.path} ${item.message}`)
              .join("；")}`,
          );
          return;
        }
        const confirmConflict =
          preflight.result.status === "needs_confirmation" &&
          window.confirm("检测到重复或同日冲突。确认保留为历史版本，不覆盖当前记录？");
        const result = await commitBoardContextImport(database, preflight, confirmConflict);
        const currentContext = await getCurrentBusinessContext(database);
        if (result.status === "committed") await onContextChange();
        setNotice(
          result.status === "committed"
            ? `BoardContext 已导入。当前来源：${
                currentContext?.source === "board_import" ? "董事会合同" : "手动填写"
              }`
            : "导入未提交；现有数据没有被覆盖。",
        );
        return;
      }
      const preflight = await preflightFullRestore(database, value, file.name);
      setRestorePreflight(preflight);
      setNotice(
        preflight.report.status === "valid"
          ? "私人备份预检通过；确认前不会修改当前数据库。"
          : `恢复预检未通过：${preflight.report.errors.map((item) => item.message).join("；")}`,
      );
    } catch (error) {
      setNotice(safeErrorMessage(error));
    }
  };

  return (
    <main className="settings-page">
      <header className="page-header">
        <span className="brand">CEO OS</span>
        <h1>设置与隐私</h1>
        <p>管理业务日期、宪法、合同文件和本地数据边界。</p>
      </header>

      {notice && (
        <div className="inline-notice" role="status">
          {notice}
        </div>
      )}

      <section className="settings-section">
        <h2>业务日期</h2>
        <label className="field">
          <span>business_timezone</span>
          <input
            aria-label="business_timezone"
            list="settings-timezones"
            value={timezoneDraft}
            onChange={(event) => {
              setTimezoneDraft(event.target.value);
              setTimezoneChangePending(false);
            }}
          />
          <small>浏览器或旅行时区变化不会自动改写它。</small>
        </label>
        <datalist id="settings-timezones">
          <option value="Asia/Shanghai" />
          <option value="Asia/Hong_Kong" />
          <option value="America/New_York" />
        </datalist>
        <button
          type="button"
          className="secondary-button"
          disabled={timezoneDraft === profile.businessTimezone}
          onClick={() => setTimezoneChangePending(true)}
        >
          查看时区更改影响
        </button>
        {timezoneChangePending && (
          <div className="restore-confirmation" role="group" aria-label="确认业务时区更改">
            <strong>
              把业务时区从 {profile.businessTimezone} 改为 {timezoneDraft}？
            </strong>
            <p>历史 date_key 不会改变；新的复盘从确认后使用新时区。</p>
            <div className="settings-actions">
              <button
                type="button"
                className="secondary-button"
                onClick={() => {
                  setTimezoneDraft(profile.businessTimezone);
                  setTimezoneChangePending(false);
                }}
              >
                取消
              </button>
              <button
                type="button"
                className="primary-button"
                onClick={() => void changeTimezone()}
              >
                确认更改业务时区
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="settings-section">
        <h2>治理默认值</h2>
        <label className="field">
          <span>新想法冷静期</span>
          <select
            value={profile.ideaCoolingHours}
            onChange={(event) => {
              const ideaCoolingHours = Math.max(24, Number(event.target.value));
              void saveProfileProgress(database, { ...profile, ideaCoolingHours })
                .then((next) => {
                  onProfileChange(next);
                  setNotice(`新想法冷静期已设为 ${ideaCoolingHours} 小时。`);
                })
                .catch((error) => setNotice(safeErrorMessage(error)));
            }}
          >
            <option value={24}>24 小时</option>
            <option value={48}>48 小时</option>
            <option value={72}>72 小时</option>
            <option value={168}>7 天</option>
          </select>
          <small>至少 24 小时；只影响以后捕获的想法，不改写已有冷静期。</small>
        </label>
        <div className="privacy-defaults">
          <div>
            <strong>Truth</strong>
            <span>默认不导出</span>
          </div>
          <div>
            <strong>Focus</strong>
            <span>仅聚合风险</span>
          </div>
          <div>
            <strong>Promise</strong>
            <span>仅重大风险，不含对手方和内容</span>
          </div>
        </div>
        <p className="section-helper">
          三个模块只在本机运行，不接外部 AI，不自动发送消息。Governance 预览必须逐字段确认。
        </p>
      </section>

      <section className="settings-section">
        <h2>共同宪法</h2>
        <p>
          当前 v{constitution?.version ?? "1.0.0"} ·{" "}
          {constitution?.approvedByUserAt ? "已由用户确认" : "尚未确认"}
        </p>
        <details className="disclosure">
          <summary>重新查看 15 条原则</summary>
          <ol className="compact-principles">
            {constitution?.principles.map((principle) => (
              <li key={principle.id}>{principle.text}</li>
            ))}
          </ol>
        </details>
      </section>

      <section className="settings-section">
        <h2>导入、备份与恢复</h2>
        <p className="section-helper">{FULL_BACKUP_WARNING}</p>
        <div className="settings-actions">
          <button
            type="button"
            className="primary-button"
            onClick={() => {
              void createFullBackup(database).then((backup) =>
                downloadJson(
                  backup,
                  `ceo-os-private-backup-${backup.produced_at.slice(0, 10)}.json`,
                ),
              );
            }}
          >
            导出完整私人备份
          </button>
          <label className="secondary-button file-button">
            导入 BoardContext 或恢复备份
            <input
              className="visually-hidden"
              type="file"
              accept="application/json,.json"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void importFile(file);
                event.target.value = "";
              }}
            />
          </label>
        </div>
        {restorePreflight?.package && (
          <div className="restore-confirmation">
            <strong>恢复将覆盖当前本地 stores</strong>
            <p>{FULL_BACKUP_WARNING}</p>
            <button
              type="button"
              className="danger-button"
              onClick={() => {
                void commitFullRestore(database, restorePreflight, true)
                  .then(async () => {
                    setRestorePreflight(undefined);
                    setNotice("完整备份已原子恢复。");
                    await onDataReset();
                  })
                  .catch((error) => setNotice(safeErrorMessage(error)));
              }}
            >
              确认覆盖并恢复
            </button>
          </div>
        )}
      </section>

      <section className="settings-section">
        <h2>离线与版本</h2>
        <p>应用版本 0.3.0 · 数据库 v{DATABASE_VERSION} · 原生 Service Worker</p>
        <button
          type="button"
          className="secondary-button"
          onClick={() => {
            void checkForPWAUpdate().then(() => setNotice("已检查可用更新。"));
          }}
        >
          检查更新
        </button>
      </section>

      <section className="settings-section danger-zone">
        <h2>永久删除</h2>
        <p>{LOCAL_SECURITY_BOUNDARY}</p>
        <button
          type="button"
          className="danger-button"
          onClick={() => {
            const confirmed = window.confirm(
              "永久删除此浏览器中的全部 CEO OS 数据？已经导出的文件不会被删除。",
            );
            if (!confirmed) return;
            void deleteAllLocalData(database)
              .then(() => initializeDatabase(database))
              .then(onDataReset)
              .catch((error) => setNotice(safeErrorMessage(error)));
          }}
        >
          永久删除全部本地数据
        </button>
      </section>
    </main>
  );
}
